import { useState, useEffect } from "react";
import { api, getDashboardStats, syncAnagrafica, syncMovimenti, getAnniDisponibili } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function ImportazioniPage() {
  const { annoCorrente } = useAuth();
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [anniList, setAnniList] = useState([]);
  // Sync per intervallo da-a (default: anno precedente → corrente)
  const [syncDa, setSyncDa] = useState(String(annoCorrente - 1));
  const [syncA, setSyncA] = useState(String(annoCorrente));
  const [log, setLog] = useState([]);

  useEffect(() => {
    setSyncDa(String(annoCorrente - 1));
    setSyncA(String(annoCorrente));
  }, [annoCorrente]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([getDashboardStats(), getAnniDisponibili()]);
        setStats(s.data);
        setAnniList(a.data.anni || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const addLog = (msg, ok = true) => setLog(prev => [{ msg, ok, time: new Date().toLocaleTimeString("it-IT") }, ...prev]);

  const getSyncYears = () => {
    const da = parseInt(syncDa);
    const a = parseInt(syncA);
    if (Number.isNaN(da) || Number.isNaN(a)) return [];
    const from = Math.min(da, a);
    const to = Math.max(da, a);
    const arr = [];
    for (let y = from; y <= to; y += 1) arr.push(y);
    return arr;
  };

  const handleSync = async (type) => {
    const years = getSyncYears();
    setSyncing(type);
    setSyncResult(null);
    try {
      for (const y of years) {
        addLog(`Sincronizzazione ${type} anno ${y}/${y + 1} in corso...`);
        if (type === "anagrafica") {
          const res = await syncAnagrafica(y);
          addLog(`Anagrafica ${y}/${y + 1}: ${res.data.totale_alunni} alunni, ${res.data.totale_genitori} genitori, ${res.data.totale_classi} classi`);
        } else {
          const res = await syncMovimenti({ anno: y });
          addLog(`Movimenti ${y}/${y + 1}: ${res.data.totale_movimenti} movimenti`);
        }
      }
      setSyncResult({ status: true, message: `Sincronizzazione ${type} completata` });
      // Refresh stats
      const s = await getDashboardStats();
      setStats(s.data);
      const a = await getAnniDisponibili();
      setAnniList(a.data.anni || []);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore nella sincronizzazione";
      addLog(msg, false);
      setSyncResult({ status: false, message: msg });
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    const years = getSyncYears();
    setSyncing("all");
    setSyncResult(null);
    try {
      for (const y of years) {
        addLog(`Sincronizzazione completa anno ${y}/${y + 1} in corso...`);
        const resAna = await syncAnagrafica(y);
        addLog(`Anagrafica ${y}/${y + 1}: ${resAna.data.totale_alunni} alunni, ${resAna.data.totale_genitori} genitori, ${resAna.data.totale_classi} classi`);
        const resMov = await syncMovimenti({ anno: y });
        addLog(`Movimenti ${y}/${y + 1}: ${resMov.data.totale_movimenti} movimenti`);
      }
      setSyncResult({ status: true, message: "Sincronizzazione completa terminata" });
      const s = await getDashboardStats();
      setStats(s.data);
      const a = await getAnniDisponibili();
      setAnniList(a.data.anni || []);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore nella sincronizzazione";
      addLog(msg, false);
      setSyncResult({ status: false, message: msg });
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div data-testid="importazioni-page">
      <div className="sw-page-title"><h1>Importazioni</h1><div className="sw-breadcrumb">Home &gt; Importazioni</div></div>

      {/* Stats */}
      {stats && (
        <div className="sw-stats-row">
          <div className="sw-stat-card"><div className="sw-stat-value">{stats.total_alunni.toLocaleString("it-IT")}</div><div className="sw-stat-label">Alunni</div></div>
          <div className="sw-stat-card"><div className="sw-stat-value">{stats.total_genitori.toLocaleString("it-IT")}</div><div className="sw-stat-label">Genitori</div></div>
          <div className="sw-stat-card"><div className="sw-stat-value">{stats.total_classi.toLocaleString("it-IT")}</div><div className="sw-stat-label">Classi</div></div>
          <div className="sw-stat-card"><div className="sw-stat-value">{stats.total_movimenti.toLocaleString("it-IT")}</div><div className="sw-stat-label">Movimenti</div></div>
        </div>
      )}

      {/* Sync Controls */}
      <div className="sw-card">
        <div className="sw-card-header">Sincronizzazione dati da Segremat Web</div>
        <div className="sw-card-body">
          <p style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>Seleziona l'anno scolastico e avvia la sincronizzazione. I dati verranno scaricati dalle API di Segremat Web e salvati nel database locale.</p>
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <span className="sw-filter-label" style={{ fontWeight: 700 }}>Sincronizza dall&apos;anno:</span>
            <input type="number" min="2000" max="2100" step="1" data-testid="sync-da-input"
              value={syncDa} onChange={e => setSyncDa(e.target.value)}
              className="sw-filter-select" style={{ width: 100, fontFamily: "monospace", textAlign: "center", fontSize: 14, fontWeight: 700 }} />
            <span className="sw-filter-label" style={{ fontWeight: 700 }}>all&apos;anno:</span>
            <input type="number" min="2000" max="2100" step="1" data-testid="sync-a-input"
              value={syncA} onChange={e => setSyncA(e.target.value)}
              className="sw-filter-select" style={{ width: 100, fontFamily: "monospace", textAlign: "center", fontSize: 14, fontWeight: 700 }} />
            <span style={{ fontSize: 11, color: "#666", fontStyle: "italic" }}>({getSyncYears().length} anni)</span>
            <span style={{ flex: 1 }} />
            <button data-testid="sync-anagrafica-btn" className="sw-filter-btn sw-filter-btn-primary" disabled={syncing} onClick={() => handleSync("anagrafica")}>
              {syncing === "anagrafica" ? "⏳ In corso..." : "▶ Anagrafica"}
            </button>
            <button data-testid="sync-movimenti-btn" className="sw-filter-btn sw-filter-btn-primary" disabled={syncing} onClick={() => handleSync("movimenti")}>
              {syncing === "movimenti" ? "⏳ In corso..." : "▶ Movimenti"}
            </button>
            <button data-testid="sync-all-btn" className="sw-filter-btn sw-filter-btn-primary" disabled={syncing} onClick={handleSyncAll} style={{ fontWeight: 700 }}>
              {syncing === "all" ? "⏳ In corso..." : "▶▶ Tutto"}
            </button>
            <button
              data-testid="dedup-movimenti-btn"
              className="sw-filter-btn"
              disabled={syncing}
              onClick={async () => {
                if (!window.confirm(
                  `🧹 Pulizia duplicati movimenti\n\n` +
                  `Rimuove i movimenti duplicati (stessa Num + anno) nel DB, mantenendo un solo record per ognuno.\n` +
                  `Crea anche l'indice unico (Num, anno) per prevenire duplicati futuri.\n\n` +
                  `Anno: ${syncA || "TUTTI"}\n\nProcedere?`
                )) return;
                try {
                  const r = await api.post("/movimenti/dedup", null, { params: syncA ? { anno: syncA } : {} });
                  const d = r.data || {};
                  alert(
                    `✅ Dedup completato\n\n` +
                    `🗑 Rimossi: ${d.documents_removed}\n` +
                    `📦 Gruppi con duplicati: ${d.groups_with_duplicates}\n` +
                    `📊 Rimanenti: ${d.remaining_documents}\n` +
                    `🔐 Indice unico creato: ${d.unique_index_created ? "sì" : "già esistente"}`
                  );
                } catch (e) {
                  alert("Errore: " + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ background: "#c62828", color: "white", fontWeight: 700 }}
              title="Rimuove duplicati movimenti (Num, anno) e crea indice unico difensivo"
            >
              🧹 Dedup Movimenti
            </button>
          </div>
          {syncing && <div style={{ marginTop: 8, fontSize: 12, color: "#0066cc" }}>⏳ Sincronizzazione in corso... potrebbe richiedere qualche minuto.</div>}
          {syncResult && <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: syncResult.status ? "#006600" : "#cc0000" }}>{syncResult.message}</div>}
        </div>
      </div>

      {/* Log */}
      <div className="sw-card">
        <div className="sw-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Log Sincronizzazione
          {log.length > 0 && <button className="sw-filter-btn" onClick={() => setLog([])} style={{ fontSize: 11 }}>Pulisci log</button>}
        </div>
        <div className="sw-card-body" style={{ maxHeight: 300, overflowY: "auto" }}>
          {log.length === 0 ? (
            <div style={{ color: "#888", fontSize: 12, textAlign: "center", padding: 20 }}>Nessuna operazione eseguita in questa sessione</div>
          ) : (
            <table className="sw-table">
              <thead><tr><th style={{ width: 80 }}>Ora</th><th>Messaggio</th><th style={{ width: 60 }}>Stato</th></tr></thead>
              <tbody>
                {log.map((l, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: "Consolas", fontSize: 11 }}>{l.time}</td>
                    <td>{l.msg}</td>
                    <td className="center-col">{l.ok ? <span style={{ color: "#006600" }}>✓</span> : <span style={{ color: "#cc0000" }}>✗</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="sw-card">
        <div className="sw-card-header">Informazioni</div>
        <div className="sw-card-body" style={{ fontSize: 12, color: "#555" }}>
          <p><strong>Anagrafica:</strong> Importa alunni, genitori, classi e le relazioni tra di loro dall'API Segremat Web.</p>
          <p><strong>Movimenti:</strong> Importa tutti i movimenti finanziari (addebiti, pagamenti, fatture) con paginazione automatica.</p>
          <p><strong>Tutto:</strong> Esegue entrambe le importazioni in sequenza.</p>
          <p style={{ marginTop: 8 }}>Ultima configurazione API: vai in <b>Impostazioni</b> per modificare le credenziali.</p>
        </div>
      </div>
    </div>
  );
}
